"use client";

import React, { useState, useEffect } from "react";
import { Shapes, Bot, Cpu, Terminal, ArrowRight, Globe, Sparkles, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface StageItem {
  id: number;
  stageNumber: string;
  ageVi: string;
  ageEn: string;
  titleVi: string;
  titleEn: string;
  summaryVi: string;
  summaryEn: string;
  detailsVi: string[];
  detailsEn: string[];
  icon: React.ElementType;
}
const STAGES: StageItem[] = [{
  id: 1,
  stageNumber: "01",
  ageVi: "4–6 tuổi",
  ageEn: "Ages 4–6",
  titleVi: "Khám Phá",
  titleEn: "Exploration",
  summaryVi: "Visual assembly focus",
  summaryEn: "Visual assembly focus",
  detailsVi: ["Nhận biết hình khối & màu sắc", "Thao tác lắp ráp cơ bản", "Tư duy không gian đầu đời"],
  detailsEn: ["Shape & color recognition", "Basic physical assembly", "Early spatial reasoning"],
  icon: Shapes
}, {
  id: 2,
  stageNumber: "02",
  ageVi: "7–9 tuổi",
  ageEn: "Ages 7–9",
  titleVi: "Chế Tạo",
  titleEn: "Creation",
  summaryVi: "Mechanical motion & block programming",
  summaryEn: "Mechanical motion & block programming",
  detailsVi: ["Cơ cấu truyền động bánh răng", "Lập trình kéo thả Scratch/LEGO", "Giải quyết vấn đề cơ bản"],
  detailsEn: ["Gear transmission mechanisms", "Scratch/LEGO block programming", "Basic problem solving"],
  icon: Bot
}, {
  id: 3,
  stageNumber: "03",
  ageVi: "10–12 tuổi",
  ageEn: "Ages 10–12",
  titleVi: "Lập Trình",
  titleEn: "Coding",
  summaryVi: "Smart sensors & control algorithms",
  summaryEn: "Smart sensors & control algorithms",
  detailsVi: ["Cảm biến khoảng cách, màu sắc", "Thuật toán rẽ nhánh & vòng lặp", "Xử lý dữ liệu đầu vào / đầu ra"],
  detailsEn: ["Distance & color sensors", "Branching & loop algorithms", "Input/output data processing"],
  icon: Cpu
}, {
  id: 4,
  stageNumber: "04",
  ageVi: "13–16 tuổi",
  ageEn: "Ages 13–16",
  titleVi: "Kỹ Sư",
  titleEn: "Engineering",
  summaryVi: "Python, microcontrollers & competition robots",
  summaryEn: "Python, microcontrollers & competition robots",
  detailsVi: ["Lập trình ngôn ngữ Python", "Vi điều khiển & mạch điện tử", "Thành lập đội thi đấu Robot"],
  detailsEn: ["Python text-based coding", "Microcontrollers & circuits", "Robotics competition teams"],
  icon: Terminal
}];
export default function CurriculumProgression() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const [activeStage, setActiveStage] = useState<number>(1);
  useEffect(() => {
    const getSavedLang = (): "vi" | "en" => {
      if (typeof window === "undefined") return "vi";
      const saved = localStorage.getItem("roboto_lang");
      return saved === "en" || saved === "vi" ? saved : "vi";
    };
    setLang(getSavedLang());
    const handleLangChange = (e: Event) => {
      const customEvent = e as CustomEvent<string>;
      if (customEvent.detail && (customEvent.detail === "vi" || customEvent.detail === "en")) {
        setLang(customEvent.detail);
      } else {
        setLang(getSavedLang());
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const handleLanguageToggle = (targetLang: "vi" | "en") => {
    setLang(targetLang);
    if (typeof window !== "undefined") {
      localStorage.setItem("roboto_lang", targetLang);
      window.dispatchEvent(new CustomEvent("roboto-lang-change", {
        detail: targetLang
      }));
    }
  };
  const isVi = lang === "vi";
  return <section id="curriculumprogression" data-nav="light" className="relative bg-background text-foreground py-16 lg:py-24 overflow-hidden border-y border-border/60">
      {/* Precision Lab Blueprint Grid Texture Background */}
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:32px_32px]" aria-hidden="true" />
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_600px_at_50%_30%,var(--ring)/0.08,transparent_100%)]" aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block with Language Control Switch */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="secondary" className="px-3 py-1 font-mono text-xs uppercase tracking-wider">
                <Sparkles className="size-3 text-secondary-foreground" />
                {isVi ? "Lộ Trình Đào Tạo ROBOTO" : "ROBOTO Learning Pathway"}
              </Badge>
              <span className="text-xs text-muted-foreground font-mono hidden sm:inline-block">
                // SPEC: ACC-416
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-foreground tracking-tight">
              {isVi ? "Lộ trình phát triển năng lực STEM & Robotics theo từng độ tuổi" : "Age-Stage STEM & Robotics Curriculum Progression"}
            </h2>

            <p className="mt-3 text-muted-foreground text-base sm:text-lg max-w-2xl">
              {isVi ? "Chương trình thiết kế nối tiếp liên tục từ nhận biết thị giác đến thiết kế vi điều khiển và phần mềm robot thi đấu chuyên nghiệp." : "A seamless 4-stage progression building skills from foundational visual assembly to advanced Python programming and competition robotics."}
            </p>
          </div>

          {/* Bilingual Switcher */}
          <div className="flex items-center gap-2 self-start md:self-end bg-muted/80 p-1 rounded-lg border border-border shrink-0">
            <Globe className="size-4 text-muted-foreground ml-2" />
            <Button variant={isVi ? "default" : "ghost"} size="sm" onClick={() => handleLanguageToggle("vi")} className="text-xs h-7 px-2.5 font-medium">
              Tiếng Việt
            </Button>
            <Button variant={!isVi ? "default" : "ghost"} size="sm" onClick={() => handleLanguageToggle("en")} className="text-xs h-7 px-2.5 font-medium">
              English
            </Button>
          </div>
        </div>

        {/* Horizontal Visual Progression Container */}
        <div className="relative mt-8">
          {/* Precision Spec Connecting Rail Line (Desktop View) */}
          <div className="hidden lg:block absolute top-[52px] left-[10%] right-[10%] h-[2px] bg-border z-0" aria-hidden="true">
            <div className="h-full bg-ring transition-all duration-500 ease-in-out" style={{
            width: `${(activeStage - 1) / (STAGES.length - 1) * 100}%`
          }} />
          </div>

          {/* Stages Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {STAGES.map((stage, index) => {
            const IconComponent = stage.icon;
            const isActive = activeStage === stage.id;
            return <div key={stage.id} data-index={index} className="group cursor-pointer" onClick={() => setActiveStage(stage.id)}>
                  <Card className={`h-full border transition-all duration-300 relative bg-card/90 backdrop-blur-xs ${isActive ? "border-ring shadow-md ring-1 ring-ring/40 -translate-y-1" : "border-border hover:border-ring/50 hover:shadow-xs"}`}>
                    <CardHeader className="pb-3 pt-5 px-5">
                      {/* Step Header Node */}
                      <div className="flex items-center justify-between mb-4">
                        <div className={`size-10 rounded-lg flex items-center justify-center font-mono font-bold text-sm transition-colors ${isActive ? "bg-secondary text-secondary-foreground shadow-xs" : "bg-muted text-muted-foreground group-hover:bg-muted/80"}`}>
                          {stage.stageNumber}
                        </div>

                        <Badge variant={isActive ? "default" : "outline"} className="font-medium text-xs px-2.5 py-0.5">
                          {isVi ? stage.ageVi : stage.ageEn}
                        </Badge>
                      </div>

                      {/* Icon & Stage Title */}
                      <div className="flex items-center gap-2.5 mb-1">
                        <div className={`p-1.5 rounded-md ${isActive ? "text-primary bg-primary/10" : "text-muted-foreground group-hover:text-foreground"}`}>
                          <IconComponent className="size-5" />
                        </div>
                        <CardTitle className="text-xl font-serif font-bold text-card-foreground">
                          {isVi ? stage.titleVi : stage.titleEn}
                          <span className="text-muted-foreground text-sm font-sans font-normal ml-1.5">
                            / {isVi ? stage.titleEn : stage.titleVi}
                          </span>
                        </CardTitle>
                      </div>

                      {/* Summary Tagline */}
                      <CardDescription className="text-xs font-semibold text-primary/90 mt-1 line-clamp-1">
                        {isVi ? stage.summaryVi : stage.summaryEn}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="px-5 pb-5 text-sm">
                      {/* Key Highlights List */}
                      <ul className="space-y-2 mt-2 pt-3 border-t border-border/60">
                        {(isVi ? stage.detailsVi : stage.detailsEn).map((detail, dIdx) => <li key={dIdx} className="flex items-start gap-2 text-xs text-muted-foreground">
                              <CheckCircle2 className={`size-3.5 shrink-0 mt-0.5 ${isActive ? "text-secondary" : "text-muted-foreground/60"}`} />
                              <span>{detail}</span>
                            </li>)}
                      </ul>

                      {/* Indicator Arrow footer */}
                      <div className="mt-4 pt-2 flex items-center justify-end text-xs font-medium text-ring opacity-0 group-hover:opacity-100 transition-opacity">
                        <span>{isVi ? "Xem chi tiết" : "Details"}</span>
                        <ArrowRight className="size-3.5 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        {/* Bottom Call to Action Box */}
        <div className="mt-12 p-6 rounded-xl bg-card border border-border shadow-2xs flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="size-12 rounded-full bg-secondary/20 flex items-center justify-center shrink-0 text-secondary-foreground font-serif font-bold text-lg">
              R
            </div>
            <div>
              <h3 className="font-serif font-bold text-foreground text-base sm:text-lg">
                {isVi ? "Bạn muốn xếp lớp phù hợp nhất cho con?" : "Find the best level for your child"}
              </h3>
              <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
                {isVi ? "Đăng ký tư vấn lộ trình học thử miễn phí và đánh giá tư duy đầu vào 1-1 cùng chuyên gia." : "Book a free trial class and 1-on-1 STEM skill assessment with our senior instructors."}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild size="default" className="w-full sm:w-auto">
              <Link to="/FreeTrialContactPage">
                <span>{isVi ? "Đăng ký học thử" : "Book Free Trial"}</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="default" className="w-full sm:w-auto">
              <Link to="/LearningPathCurriculumPage">
                <span>{isVi ? "Xem toàn bộ giáo trình" : "Full Curriculum"}</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}